import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  GridSizeChangedEvent,
  RowSelectionOptions,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { Observable, combineLatest, debounceTime, filter } from 'rxjs';
import { CommonTranslateService } from 'src/app/common/translate/translate.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { debugLog, filterTrue } from '../../pipe/rxjs/operators';
import { RowSelectionService } from '../../service/commission/row-selection.service';
import { GridService } from '../../service/grid.service';
import { PaginationService } from '../../store/pagination/pagination.service';
import { SearchService } from '../../store/search/search.service';
import {
  ActionType,
  Column,
  NavigationObject,
  View,
  ViewType,
} from '../../store/view/types';
import { ViewService } from '../../store/view/views.service';
import { SearchResult } from '../../types';
import { Filter } from '../../store/search/types';
import { ExportDataService } from '../../service/export/export-data.service';
import { ButtonType } from '../../store/subheader/type';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { OffcanvasContentComponent } from '../offcanvas-content/offcanvas-content.component';

@Component({
  selector: 'app-data-listing-provider',
  templateUrl: './data-listing-provider.component.html',
  styleUrls: ['./data-listing-provider.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class DataListingProviderComponent implements OnInit, OnDestroy {
  protected readonly isAllRowsSelected = signal<boolean>(false);
  protected readonly isTotalCountSelectable = signal<boolean>(false);
  private readonly totalCount = signal<number>(-1);
  protected readonly numberOfTotalRowsSelectable = signal<number>(-1);
  private readonly isTotalCountSelectedFlag = signal<boolean>(false);
  protected readonly totalCountSelectedLabelKey = signal<string>(
    'GLOBAL_LBL_SELECT_ALL_FILTERED',
  );
  protected readonly isTotalCountSelected = computed(
    () => this.isTotalCountSelectedFlag() && this.isAllRowsSelected(),
  );

  private readonly COLUMN_MIN_WIDTH = 50;
  private readonly COLUMN_MAX_WIDTH = 300;

  data$: Observable<SearchResult<any>>;
  view$: Observable<View>;
  columnDefs: ColDef[];
  gridOptions: GridOptions = {};
  rowSelectionOptions: RowSelectionOptions | undefined;
  noRowsTemplate: string;
  viewType$: Observable<ViewType>;
  onActionClickedSetRowData = output<any>();
  context: any;
  apicall = this.searchService.search;

  private treeViewCellContentWidth: boolean | undefined | null = undefined;
  private autoSizeColumnIds: string[] = [];
  private viewName: string | undefined = undefined;

  protected offcanvasTitle = '';
  protected offcanvasColumns: Column[] = [];

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  protected readonly ButtonType = ButtonType;

  constructor(
    private readonly paginationService: PaginationService,
    private readonly searchService: SearchService,
    private readonly router: Router,
    private readonly viewService: ViewService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly gridService: GridService,
    private readonly translate: CommonTranslateService,
    private readonly rowSelectionService: RowSelectionService,
    private readonly exportDataService: ExportDataService,
    private readonly offcanvasService: NgbOffcanvas,
  ) {
    this.context = {
      componentParent: this,
    };
  }

  ngOnInit(): void {
    this.view$ = this.viewService.getViews.value$;
    this.viewType$ = this.viewService.getViewType();

    this.translate.get('AG_GRID_NO_ROWS_TO_SHOW').subscribe((msg) => {
      this.noRowsTemplate = msg;
    });

    //get default grid options from service
    this.gridOptions = this.gridService.getConfig();

    this.view$.pipe(filterTrue(), untilDestroyed(this)).subscribe((view) => {
      //Set builtInFilterGroups from view
      const builtinActiveFilterGroups =
        view.searchView.builtInFilterGroups.filter((filterGroup) =>
          filterGroup.filters.some((filter) =>
            filter.domains.some((domain) => domain.isActive),
          ),
        );
      for (const filterGroup of builtinActiveFilterGroups) {
        for (const filter of filterGroup.filters) {
          if (filter.domains.some((domain) => domain.isActive)) {
            this.searchService.addDefaultActiveFilter(filter, false);
          }
        }
      }

      //Set permanentFilters from view
      const permanentActiveFilters = view.searchView.permanentFilters?.filter(
        (filter) => filter.domains.some((domain) => domain.isActive),
      );

      if (permanentActiveFilters) {
        for (const filter of permanentActiveFilters) {
          this.searchService.addDefaultActiveFilter(filter, false);
        }
        //deactive if there is smartFilter
        this.searchService.temporaryDeactivatePermanentFilters();
      }

      //Set propertyFilters from view
      const propertyActiveFilters = view.searchView.propertyFilters?.filter(
        (filter) =>
          filter.items.some((item) =>
            item.domains.some((domain) => domain.isActive),
          ),
      );

      if (propertyActiveFilters) {
        for (const propertyActiveFilter of propertyActiveFilters) {
          for (const item of propertyActiveFilter.items) {
            if (!item.domains.some((domain) => domain.isActive)) continue;

            const filter: Filter = {
              description: '',
              domains: item.domains,
              isReadOnly: propertyActiveFilter.isReadOnly,
              name: propertyActiveFilter.propertyId + '/' + item.label,
              tag: propertyActiveFilter.tag,
              text: item.label,
            };

            this.searchService.addDefaultActiveFilter(filter, false);
          }
        }
        this.searchService.deactivatePropertyFiltersIfSmartActionsEnabled();
      }
      this.searchService.updateHopNumber(this.router.url);

      //Set breadcrumb
      this.breadcrumbService.set('@list', view.title);

      //has offcanvas ?
      const hasOffcanvasView =
        !!view.offcanvasView &&
        !!view.offcanvasView.columns &&
        view.offcanvasView.columns.length > 0;
      if (hasOffcanvasView) {
        this.offcanvasColumns = view.offcanvasView.columns;
        this.offcanvasTitle = view.offcanvasView.title;
      }
      //Set Default view
      let viewType = view.views.split(' ')[0] as ViewType;
      this.viewService.setViewType(viewType);

      //Setup grid columns
      this.treeViewCellContentWidth = view.treeView.cellContentWidth;
      this.columnDefs = [];
      this.autoSizeColumnIds = [];
      view.treeView.columns.forEach((column) => {
        this.columnDefs.push(
          this.getColumnDef(
            column,
            this.treeViewCellContentWidth,
            view.treeView.cellMinWidth,
            view.treeView.cellMaxWidth,
          ),
        );
        if (this.treeViewCellContentWidth && column.width === 0) {
          this.autoSizeColumnIds.push(column.name);
        }
      });
      if (view.treeView.defaultSort) {
        let indexDefaultSort = this.columnDefs.findIndex(
          (column) => column.field === view.treeView.defaultSort,
        );
        if (indexDefaultSort >= 0) {
          // this.columnDefs[indexDefaultSort].sort = view.list.orderBy;
          switch (view.treeView.orderBy) {
            case 'asc':
              this.columnDefs[indexDefaultSort].sort = 'asc';
              break;
            case 'desc':
              this.columnDefs[indexDefaultSort].sort = 'desc';
              break;
            default:
              this.columnDefs[indexDefaultSort].sort = null;
              break;
          }
        }
      }

      //offcanvas column
      if (hasOffcanvasView) {
        const offcanvasColumn = this.gridService.getOffcanvasColumnDef();
        this.columnDefs.push(offcanvasColumn);
      }

      this.gridOptions.columnDefs = this.columnDefs;

      //Set grid actions
      let navigationObject: NavigationObject = {
        left: undefined,
        middle: undefined,
      };
      view.treeView.actions.forEach((action) => {
        if (action.mode === 'Navigate') {
          let type: ActionType = '';
          let url: string = '';
          let idProperty: string = action.idProperty;
          switch (action.actionName) {
            case 'openDetail':
              type = 'openDetail';
              url = [
                this.router.url.substring(0, this.router.url.lastIndexOf('/')),
                'Details',
              ].join('/');
              break;
            case 'openBlankDetail':
              type = 'openBlankDetail';
              url = [
                this.router.url.substring(0, this.router.url.lastIndexOf('/')),
                'Details',
              ].join('/');
              break;
            case 'openExternalDetail':
              type = 'openExternal';
              url = action.route;
              break;
            case 'openExternalBlankDetail':
              type = 'openExternalBlank';
              url = action.route;
              break;
            default:
              type = '';
              url = '';
              idProperty = '';
              break;
          }

          if (action.name === 'leftClick') {
            navigationObject.left = {
              type: type,
              url: url,
              idProperty: idProperty,
            };
          }
          if (action.name === 'middleClick') {
            navigationObject.middle = {
              type: type,
              url: url,
              idProperty: idProperty,
            };
          }
        }
        if (action.mode === 'Action') {
          if (action.name === 'rowSelection') {
            this.viewName = view.name;
            this.rowSelectionOptions = this.gridService.getRowSelectionConfig(
              view.name,
            );
            this.isTotalCountSelectable.set(
              view.treeView.isTotalCountSelectable,
            );
            if (
              !!view.treeView.totalCountSelectedLabelKey &&
              view.treeView.totalCountSelectedLabelKey.length > 0
            )
              this.totalCountSelectedLabelKey.set(
                view.treeView.totalCountSelectedLabelKey,
              );
          }
        }
      });

      if (navigationObject) {
        this.gridOptions.onCellMouseDown = (event) => {
          //la cellule gère elle-même le clic (lien, action, offcanvas, sélection)
          if (this.gridService.isRowNavigationSuppressed(event)) return;

          if (event.event && event.event['button'] === 1) {
            //middle click
            const middle = navigationObject.middle;
            if (middle) {
              const url: string =
                middle.url +
                (middle.url[middle.url.length - 1] === '/' ? '' : '/') +
                event.data[middle.idProperty];

              if (
                middle.type === 'openDetail' ||
                middle.type === 'openExternal'
              ) {
                this.router.navigate([url]);
              }
              if (
                middle.type === 'openBlankDetail' ||
                middle.type === 'openExternalBlank'
              ) {
                this.router.navigate([]).then((result) => {
                  window.open(url, '_blank');
                });
              }
            }
          } else {
            //leftclick
            const left = navigationObject.left;
            if (left) {
              const url: string =
                left.url +
                (left.url[left.url.length - 1] === '/' ? '' : '/') +
                event.data[left.idProperty];

              if (left.type === 'openDetail' || left.type === 'openExternal') {
                this.router.navigate([url]);
              }
              if (
                left.type === 'openBlankDetail' ||
                left.type === 'openExternalBlank'
              ) {
                this.router.navigate([]).then((result) => {
                  window.open(url, '_blank');
                });
              }
            }
          }
        };
      }

      //Set the set and order by in pagination service
      this.paginationService.setSortBy(view.treeView.defaultSort);
      this.paginationService.setOrderBy(view.treeView.orderBy);

      if (this.agGrid != undefined) {
        if (this.treeViewCellContentWidth) {
          this.agGrid.api.autoSizeColumns(this.autoSizeColumnIds);
        } else {
          this.agGrid.api.sizeColumnsToFit();
        }

        //Update the grid Options
        this.agGrid.api.updateGridOptions(this.gridOptions);
      }
    });

    this.data$ = this.searchService.search.value$;
    combineLatest([
      this.viewService.getViews.value$,
      this.searchService.getActiveFilters(),
      this.paginationService.getPageIndex(),
      this.paginationService.getPageSize(),
      this.paginationService.getSortBy(),
      this.paginationService.getOrderBy(),
    ])
      .pipe(
        filter(
          ([view, activeFilter, pageIndex, pageSize, sortBy, orderBy]) =>
            !!view &&
            !!activeFilter &&
            !!pageIndex &&
            !!pageSize &&
            !!sortBy &&
            !!orderBy,
        ),
        debounceTime(200),
        untilDestroyed(this),
      )
      .subscribe(
        ([view, activeFilters, pageIndex, pageSize, sortBy, orderBy]) => {
          let filters = JSON.parse(JSON.stringify(activeFilters));
          this.searchService.search.call({
            filters: activeFilters,
            url: view.searchView.url,
            pageIndex: pageIndex,
            pageSize: pageSize,
            sortBy: sortBy,
            orderBy: orderBy,
          });
        },
      );
    this.data$
      .pipe(filterTrue(), untilDestroyed(this))
      .subscribe((searchResult) => {
        this.paginationService.setTotalCount(searchResult.totalCount);
        this.paginationService.setPageSize(searchResult.pageSize);
        this.paginationService.setTotalPages(searchResult.totalPages);

        this.totalCount.set(searchResult.totalCount);
      });
  }

  ngOnDestroy(): void {
    this.searchService.search.reset();
    this.searchService.setSelectedRows({
      data: [],
      isTotalRowCountSelected: false,
    });
    this.isAllRowsSelected.set(false);
    this.totalCount.set(-1);
    this.numberOfTotalRowsSelectable.set(-1);
    this.isTotalCountSelectedFlag.set(false);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridOptions.columnDefs = this.columnDefs;
    if (this.treeViewCellContentWidth) {
      params.api.autoSizeColumns(this.autoSizeColumnIds);
    } else {
      params.api.sizeColumnsToFit();
    }
    params.api.updateGridOptions(this.gridOptions);
  }

  setRowData(rowData: any): void {
    this.onActionClickedSetRowData.emit(rowData);
  }

  onSortChanged(event): void {
    this.searchService.search.reset();
    const sortedColumn = event.api
      .getColumnState()
      .find((col) => Boolean(col.sort));
    if (sortedColumn != undefined) {
      this.paginationService.setSortBy(sortedColumn.colId);
      this.paginationService.setOrderBy(sortedColumn.sort);
    } else {
      this.view$
        .pipe(debounceTime(200), untilDestroyed(this))
        .subscribe((view) => {
          this.paginationService.setSortBy(view.treeView.defaultSort);
          this.paginationService.setOrderBy(view.treeView.orderBy);
        });
    }
  }

  onRowDataUpdated(): void {
    if (this.treeViewCellContentWidth) {
      this.agGrid.api.autoSizeColumns(this.autoSizeColumnIds);
    }

    let i = 0;
    this.agGrid.api.forEachNode((node) => {
      if (node.selectable) i++;
    });
    this.numberOfTotalRowsSelectable.set(i);
  }

  onGridSizeChanged($event: GridSizeChangedEvent) {
    if (this.treeViewCellContentWidth) {
      this.agGrid.api.autoSizeColumns(this.autoSizeColumnIds);
    } else {
      this.agGrid.api.sizeColumnsToFit();
    }
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedRows();

    if (this.isTotalCountSelectable()) {
      const selectable = this.numberOfTotalRowsSelectable();
      const allSelected = selectable >= 0 && selectedRows.length === selectable;
      const wasAllSelected = this.isAllRowsSelected();

      this.isAllRowsSelected.set(allSelected);

      // ne réinitialise le mode "total count" que sur une vraie transition,
      // sinon un re-render d'ag-grid casserait silencieusement la sélection étendue
      if (wasAllSelected !== allSelected) {
        this.isTotalCountSelectedFlag.set(false);
      }
    }

    this.pushSelectedRows(selectedRows);
  }

  private pushSelectedRows(totalRowSelected: any[]) {
    if (this.isTotalCountSelected()) {
      this.searchService.setSelectedRows({
        data: [],
        isTotalRowCountSelected: true,
      });
    } else {
      this.searchService.setSelectedRows({
        data: totalRowSelected,
        isTotalRowCountSelected: false,
      });
    }
  }

  getColumnDef(
    column: Column,
    cellContentWidth?: boolean | null,
    cellMinWidth?: number,
    cellMaxWidth?: number,
  ): ColDef<any> {
    let colDef: ColDef = {
      field: column.name,
      headerName: column.title,
      sortable: column.sortable,
      suppressMovable: true, //column not movable for all
      sort: null,
      sortingOrder: ['asc', 'desc'],
      resizable: false,
      unSortIcon: true, //Icon to show sort is possible
      autoHeaderHeight: true,
      wrapHeaderText: true,
    };

    if (column.width != 0) {
      colDef.width = column.width; // Largeur fixe
      colDef.suppressSizeToFit = true; // Ne pas redimensionner cette colonne
    } else {
      if (cellContentWidth) {
        // Colonnes sans width : dimensionnées par AG Grid automatiquement
        colDef.minWidth =
          column.minWidth != 0
            ? column.minWidth
            : cellMinWidth && cellMinWidth != 0
              ? cellMinWidth
              : this.COLUMN_MIN_WIDTH;
        colDef.maxWidth =
          column.maxWidth != 0
            ? column.maxWidth
            : cellMaxWidth && cellMaxWidth != 0
              ? cellMaxWidth
              : this.COLUMN_MAX_WIDTH;
        colDef.width = undefined;
      }
    }
    // cellContentWidth = true : autoSizeColumns() ajuste sur le contenu des colonnes sans width fixe
    return this.gridService.renderCustomColumn(column, colDef);
  }

  protected onClickSetFlagIsAllSelected() {
    const next = !this.isTotalCountSelectedFlag();
    this.isTotalCountSelectedFlag.set(next);
    this.pushSelectedRows(next ? [] : this.agGrid?.api.getSelectedRows() ?? []);
  }

  //offcanvas
  openOffcanvas(row: any): void {
    if (!this.offcanvasColumns.length) return;
    const ref = this.offcanvasService.open(OffcanvasContentComponent, {
      position: 'end',
      scroll: true,
    });
    ref.componentInstance.title = this.offcanvasTitle;
    ref.componentInstance.fields = this.offcanvasColumns;
    ref.componentInstance.row = row;
  }
}
